import { Component, EventEmitter, Input, Output, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { NgClass } from '@angular/common';
import { SpinnerComponent } from '../../../../../lib/components/spinner/spinner.component';
import { FaIconComponent } from '@src/lib/components/fa-icon/fa-icon.component';
import { TileProcessingItem, TileProcessingStatus } from '../../../shared/models/csv-tile';
import { CSVConfigExporter } from '../../../shared/utils/csv-config-exporter.util';
import { CSVFileValidator } from '../../../shared/utils/csv-file-validator.util';
import { TileValidationService } from '../../../../../core/services/tile-validation.service';
import { ShadeToolsService } from '../../../../../core/services/shade-tools.service';
import { DialogBoxButtonColor } from '../../../../../shared/models/dialog-box';
import * as Papa from 'papaparse';

@Component({
  selector: 'motn-bulk-tile-configuration-upload',
  templateUrl: './bulk-tile-configuration-upload.component.html',
  styleUrls: ['./bulk-tile-configuration-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    SpinnerComponent,
    FaIconComponent,
  ],
})
export class BulkTileConfigurationUploadComponent {
  @Output() close = new EventEmitter<void>();
  @Output() proceedToProcessing = new EventEmitter<TileProcessingItem[]>();

  selectedFile: File | null = null;
  processingUpload: boolean = false;

  constructor(
    private tileValidator: TileValidationService,
    private shadeToolsService: ShadeToolsService
  ) {}

  @HostListener('window:keydown.enter', ['$event'])
  onEnterKey(event: KeyboardEvent): void {
    // Only process if file is selected and not already processing
    if (this.selectedFile && !this.processingUpload) {
      event.preventDefault();
      this.processFile();
    }
  }

  @HostListener('window:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    event.preventDefault();
    this.onCloseDialog();
  }

  onCloseDialog(): void {
    // Show confirmation if currently processing
    if (this.processingUpload) {
      this.shadeToolsService.startDialog(
        '<i class="fa-regular fa-circle-stop"></i> Stop Processing?',
        'CSV file is still being processed. Closing will stop the validation and return you to the previous screen.',
        [
          {
            text: 'Stop',
            color: DialogBoxButtonColor.Red,
            callback: () => {
              this.processingUpload = false;
              this.close.emit();
            }
          },
          {
            text: 'Cancel',
            color: DialogBoxButtonColor.Alt,
            callback: () => {
              // Just close the dialog
            }
          }
        ],
        false, false, true, true
      );
    } else {
      this.close.emit();
    }
  }

  downloadNumericTemplate(): void {
    CSVConfigExporter.downloadNumericConfigTemplate().subscribe({
      error: (error) => {
        console.error('Failed to download template:', error);
        this.shadeToolsService.errorDialog(`Failed to download template: ${error.message || 'Unknown error'}`);
      }
    });
  }

  downloadToggleTemplate(): void {
    CSVConfigExporter.downloadToggleConfigTemplate().subscribe({
      error: (error) => {
        console.error('Failed to download template:', error);
        this.shadeToolsService.errorDialog(`Failed to download template: ${error.message || 'Unknown error'}`);
      }
    });
  }

  tileGlossaryNewTab(): void {
    window.open('/tile-glossary', '_blank');
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      // Validate file using utility
      const validationError = CSVFileValidator.validateFile(file);
      if (validationError) {
        this.shadeToolsService.errorDialog(validationError);
        this.selectedFile = null;
        return;
      }

      this.selectedFile = file;
    }
  }

  formatFileSize(bytes: number): string {
    return CSVFileValidator.formatFileSize(bytes);
  }

  async processFile(): Promise<void> {
    if (!this.selectedFile) {
      this.shadeToolsService.errorDialog('Please select a file to upload');
      return;
    }

    this.processingUpload = true;

    try {
      const csvText = await this.selectedFile.text();

      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            const processingItems = this.validateAndParseCSV(results.data);
            this.proceedToProcessing.emit(processingItems);
          } catch (error: any) {
            this.shadeToolsService.errorDialog(error.message);
            this.processingUpload = false;
          }
        },
        error: (error) => {
          this.shadeToolsService.errorDialog(`CSV parsing failed: ${error.message}`);
          this.processingUpload = false;
        }
      });
    } catch (error: any) {
      this.shadeToolsService.errorDialog(`Failed to read file: ${error.message}`);
      this.processingUpload = false;
    }
  }

  private validateAndParseCSV(data: any[]): TileProcessingItem[] {
    if (!data || data.length === 0) {
      throw new Error('CSV file is empty');
    }

    // Validate that Tile ID column exists
    const firstRow = data[0];
    if (!('Tile ID' in firstRow)) {
      throw new Error('CSV must contain a "Tile ID" column');
    }

    // Parse rows into processing items
    const items: TileProcessingItem[] = [];
    const allErrors: string[] = [];

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const tileId = row['Tile ID']?.trim();
      const rowNumber = i + 2;

      if (!tileId) {
        allErrors.push(`Row ${rowNumber}: Missing Tile ID`);
        continue;
      }

      // Validate configuration values
      const validationErrors = this.validateConfigurationRow(row, rowNumber);
      if (validationErrors.length > 0) {
        allErrors.push(...validationErrors);
        continue;
      }

      items.push({
        id: `config-${i}`,
        tileId: tileId,
        status: TileProcessingStatus.QUEUED,
        csvData: row
      });
    }

    // Fail fast if any validation errors
    if (allErrors.length > 0) {
      const errorMessage = allErrors.slice(0, 5).join('\n') +
        (allErrors.length > 5 ? `\n...and ${allErrors.length - 5} more errors` : '');
      throw new Error(errorMessage);
    }

    return items;
  }

  private validateConfigurationRow(row: any, rowNumber: number): string[] {
    const errors: string[] = [];

    // Validate decimal places if provided
    if (row['Decimal Places'] !== undefined && row['Decimal Places'] !== '') {
      const result = this.tileValidator.validateDecimalPlaces(parseInt(row['Decimal Places'], 10));
      if (result.errors.length > 0) {
        errors.push(`Row ${rowNumber}: ${result.errors.join(', ')}`);
      }
    }

    // Validate multiplier if provided
    if (row['Multiplier'] !== undefined && row['Multiplier'] !== '') {
      const result = this.tileValidator.validateMultiplier(row['Multiplier'].toString());
      if (result.errors.length > 0) {
        errors.push(`Row ${rowNumber}: ${result.errors.join(', ')}`);
      }
    }

    // Validate offset if provided
    if (row['Calibration Offset'] !== undefined && row['Calibration Offset'] !== '') {
      const result = this.tileValidator.validateOffset(parseFloat(row['Calibration Offset']));
      if (result.errors.length > 0) {
        errors.push(`Row ${rowNumber}: ${result.errors.join(', ')}`);
      }
    }

    // Validate conversion formula if provided
    const conversionFormula = row['Transformation Conversion Formula']?.toLowerCase() === 'none' ? '' : row['Transformation Conversion Formula'];
    if (conversionFormula !== undefined && conversionFormula !== '') {
      const result = this.tileValidator.validateConversionFormula(conversionFormula);
      if (result.errors.length > 0) {
        errors.push(`Row ${rowNumber}: ${result.errors.join(', ')}`);
      }
    }

    if (row['Auto-Clear Alarms'] !== undefined && row['Auto-Clear Alarms'] !== '') {
      const result = this.tileValidator.validateAutoClear(row['Auto-Clear Alarms']);
      if (result.errors.length > 0) {
        errors.push(`Row ${rowNumber}: ${result.errors.join(', ')}`);
      }
    }

    // Validate min/max writable values if both provided
    if (row['Minimum Writable Value'] !== undefined && row['Minimum Writable Value'] !== '' &&
        row['Maximum Writable Value'] !== undefined && row['Maximum Writable Value'] !== '') {
      const min = parseFloat(row['Minimum Writable Value']);
      const max = parseFloat(row['Maximum Writable Value']);
      const result = this.tileValidator.validateMinMax(min, max);
      if (result.errors.length > 0) {
        errors.push(`Row ${rowNumber}: ${result.errors.join(', ')}`);
      }
    }

    return errors;
  }
}
